import { z } from "zod";

/**
 * The submissions-table query contract — docs/01-PRODUCT-SPEC.md §9.1, docs/03-API-SPEC.md §6.
 *
 * One schema, used by the dashboard to build a request and by the API to parse it. A saved view
 * stores exactly this shape, which is why it lives in `@sga/shared` rather than in either app: a
 * view saved by the builder must be readable by the server three months later without a translation
 * layer between them drifting.
 */

/**
 * Field keys are `snake_case` and stable after publish (CLAUDE.md). Enforcing the shape here is not
 * decoration: these keys are interpolated into `data->>'key'` SQL, and a key that cannot contain a
 * quote is the difference between a parameterised query builder and an injection surface.
 */
const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,62}$/;

/** The submission's own columns, addressable in a filter alongside form fields. */
export const SUBMISSION_META_FIELDS = [
  "status",
  "reviewStatus",
  "submittedAt",
  "lastSavedAt",
  "source",
  "uniqueId",
  "randomId",
  "duplicateFlag",
  "respondentEmail",
  // The person, not an answer: a form is free to ask for a name in a field of its own, and
  // most do. This is the canonical profile's name, which is what the "Applicant" column shows
  // and — until now — the one column in the table nothing could sort by.
  "respondentName",
  "utm.source",
  "utm.medium",
  "utm.campaign",
] as const;

export type SubmissionMetaField = (typeof SUBMISSION_META_FIELDS)[number];

/**
 * A filter target is either a form field (`data.nin_number`) or one of the submission's own
 * columns. The `data.` prefix is what keeps the two namespaces apart — a form is free to have a
 * field called `status` without it colliding with the submission's review workflow.
 */
export const isMetaField = (value: string): value is SubmissionMetaField =>
  (SUBMISSION_META_FIELDS as readonly string[]).includes(value);

export const filterTarget = z
  .string()
  .max(70)
  .refine(
    (value) =>
      (value.startsWith("data.") && FIELD_KEY_PATTERN.test(value.slice(5))) || isMetaField(value),
    { message: "not a filterable field" },
  );

export const submissionFilterSchema = z.discriminatedUnion("op", [
  z.object({ field: filterTarget, op: z.literal("contains"), value: z.string().min(1).max(200) }),
  z.object({
    field: filterTarget,
    op: z.literal("in"),
    values: z
      .array(z.union([z.string(), z.number(), z.boolean()]))
      .min(1)
      .max(100),
  }),
  z.object({
    field: filterTarget,
    op: z.literal("eq"),
    value: z.union([z.string(), z.number(), z.boolean()]),
  }),
  z.object({
    field: filterTarget,
    op: z.literal("numberBetween"),
    min: z.number().optional(),
    max: z.number().optional(),
  }),
  z.object({
    field: filterTarget,
    op: z.literal("dateBetween"),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
  }),
  z.object({ field: filterTarget, op: z.literal("isEmpty") }),
  z.object({ field: filterTarget, op: z.literal("isNotEmpty") }),
]);

export type SubmissionFilter = z.infer<typeof submissionFilterSchema>;

export const submissionSortSchema = z.object({
  field: filterTarget,
  direction: z.enum(["asc", "desc"]).default("desc"),
});

export type SubmissionSort = z.infer<typeof submissionSortSchema>;

export const DEFAULT_SUBMISSION_SORT: SubmissionSort = { field: "submittedAt", direction: "desc" };

/**
 * `filters` and `sort` arrive as JSON strings in the query string (docs/03-API-SPEC.md §6) because
 * a nested filter array does not survive a flat query encoding in any readable form.
 */
const jsonParam = <T extends z.ZodTypeAny>(inner: T) =>
  z
    .string()
    .transform((value, ctx): unknown => {
      try {
        return JSON.parse(value);
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "not valid JSON" });
        return z.NEVER;
      }
    })
    .pipe(inner);

export const submissionListQuerySchema = z.object({
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  q: z.string().trim().min(2).max(200).optional(),
  status: z.string().optional(),
  reviewStatus: z.string().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  filters: jsonParam(z.array(submissionFilterSchema).max(25)).optional(),
  sort: jsonParam(submissionSortSchema).optional(),
  viewId: z.string().optional(),
  // Test submissions are excluded everywhere by default (spec §9, §10) — a coordinator testing a
  // form must not move the numbers a programme is judged on.
  includeTest: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  columns: jsonParam(z.array(z.string().max(80)).max(200)).optional(),
});

export type SubmissionListQuery = z.output<typeof submissionListQuerySchema>;

export const savedViewBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  columns: z.array(z.string().max(80)).max(200).default([]),
  filters: z.array(submissionFilterSchema).max(25).default([]),
  sort: submissionSortSchema.default(DEFAULT_SUBMISSION_SORT),
  isShared: z.boolean().default(false),
  isDefault: z.boolean().default(false),
});

export type SavedViewBody = z.infer<typeof savedViewBodySchema>;

export const savedViewPatchSchema = savedViewBodySchema.partial();

export const globalSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(200),
  programmeId: z.string().optional(),
  cycleId: z.string().optional(),
  formId: z.string().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export type GlobalSearchQuery = z.output<typeof globalSearchQuerySchema>;

export const submissionPatchSchema = z.object({
  // A staff correction is an edit to someone else's answer. The reason is not optional and not a
  // formality — it is what the applicant is shown if they ever ask why their answer changed.
  reason: z.string().trim().min(5).max(500),
  values: z.record(z.string(), z.unknown()).refine((value) => Object.keys(value).length > 0, {
    message: "at least one field must be corrected",
  }),
});

export type SubmissionPatch = z.infer<typeof submissionPatchSchema>;

/**
 * Reading a sensitive field — a NIN, a bank account number — back out of the encrypted store.
 *
 * The reason is mandatory for the same purpose it is on a correction, and a stronger one: this is
 * the only path by which a value encrypted at rest ever reaches a human, and the audit row it
 * writes is the entire accountability story for that access. "Who looked at this applicant's NIN,
 * when, and why" has to be answerable years later.
 *
 * Fields are named explicitly rather than revealed wholesale: asking for a bank account number
 * should not also hand over a NIN.
 */
export const submissionRevealSchema = z.object({
  reason: z.string().trim().min(5).max(500),
  fieldKeys: z.array(z.string().min(1)).min(1, "name at least one field to reveal").max(20),
});

export type SubmissionReveal = z.infer<typeof submissionRevealSchema>;
